// Session state for the email/password login. The token is a PocketBase user
// token; config.ts still owns the Authorization header, so every successful
// login and refresh writes through setToken().

import { SERVER_URL, setToken } from "./config";

export interface Session {
  token: string;
  expiresAt: number;
  email: string;
  name?: string;
}

/** The three outcomes the login screen must tell apart in plain Swedish. */
export type LoginErrorKind = "invalid_login" | "network" | "server";

export class LoginError extends Error {
  constructor(readonly kind: LoginErrorKind) {
    super(kind);
  }
}

// NOT "phoenix.session" — that key is the chat session id (views/prata.ts).
const AUTH_KEY = "phoenix.auth";

interface AuthResponse {
  token: string;
  expires_at: number;
  user: { id: string; email: string; name?: string };
}

function store(body: AuthResponse): Session {
  const session: Session = {
    token: body.token,
    expiresAt: Number(body.expires_at) || 0,
    email: body.user?.email ?? "",
    ...(body.user?.name ? { name: body.user.name } : {}),
  };
  try {
    localStorage.setItem(AUTH_KEY, JSON.stringify(session));
  } catch {
    /* storage full — the session then lives for this page load only */
  }
  setToken(session.token);
  return session;
}

export async function login(email: string, password: string): Promise<Session> {
  let res: Response;
  try {
    res = await fetch(`${SERVER_URL}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.trim(), password }),
    });
  } catch {
    throw new LoginError("network");
  }
  if (res.status === 401) throw new LoginError("invalid_login");
  if (!res.ok) throw new LoginError("server");
  let body: AuthResponse;
  try {
    body = (await res.json()) as AuthResponse;
  } catch {
    throw new LoginError("server");
  }
  if (!body?.token) throw new LoginError("server");
  return store(body);
}

/** Renews the token in place. Never throws: a failure just means "not now". */
export async function refresh(): Promise<Session | null> {
  const current = getSession();
  if (!current) return null;
  try {
    const res = await fetch(`${SERVER_URL}/api/auth/refresh`, {
      method: "POST",
      headers: { Authorization: `Bearer ${current.token}` },
    });
    if (!res.ok) return null;
    const body = (await res.json()) as AuthResponse;
    if (!body?.token) return null;
    return store(body);
  } catch {
    return null; // network trouble: keep the session and try again later
  }
}

export function getSession(): Session | null {
  let parsed: Partial<Session>;
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    parsed = JSON.parse(raw) as Partial<Session>;
  } catch {
    return null;
  }
  if (typeof parsed.token !== "string" || !parsed.token) return null;
  return {
    token: parsed.token,
    expiresAt: typeof parsed.expiresAt === "number" ? parsed.expiresAt : 0,
    email: typeof parsed.email === "string" ? parsed.email : "",
    ...(typeof parsed.name === "string" && parsed.name ? { name: parsed.name } : {}),
  };
}

/** Stateless tokens — logging out is purely local. */
export function logout(): void {
  try {
    localStorage.removeItem(AUTH_KEY);
  } catch {
    /* nothing to clean up */
  }
  setToken("");
}
